import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, ShieldCheck, AlertCircle } from 'lucide-react';
import {
  CHECK_STAGES,
  CHECK_STAGE_LABEL,
  type CheckStage,
  type PreflightKey,
  type RejectEmailRecipient,
} from '@hcc/types';
import { ApiError } from '../../../lib/api';
import {
  useAdminChecks,
  useConfirmCheck,
  useMarkCheckApplied,
  useMarkCheckProcessed,
  useSendBackCheck,
  type AdminCheckRow,
} from '../../../hooks/useAdminChecks';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { ConfirmModal } from '../../../components/ui/ConfirmModal';
import { Pager } from '../../../components/ui/Pager';
import { RejectTaskModal } from '../../../components/onboarding/RejectTaskModal';
import { CheckRow } from './CheckRow';

/**
 * Per-check-type config. Adding DVLA later = one entry here plus its
 * nav leaf; the route, hooks, and API are already generic.
 */
const CHECK_CONFIG: Partial<Record<PreflightKey, { title: string; description: string }>> = {
  dbs: {
    title: 'DBS checks',
    description:
      'Everyone who has been given the DBS check task, through application, portal processing, and the day-one physical certificate check.',
  },
  dvla: {
    title: 'DVLA checks',
    description:
      'Everyone who has been given the DVLA check task, through application, processing, and verification.',
  },
};

const PAGE_SIZE = 25;

export function ChecksAdmin() {
  const { checkType } = useParams<{ checkType: string }>();
  const config = CHECK_CONFIG[checkType as PreflightKey];

  const [q, setQ] = useState('');
  const [stage, setStage] = useState<CheckStage | ''>('');
  const [page, setPage] = useState(1);
  const [verifying, setVerifying] = useState<AdminCheckRow | null>(null);
  const [sendingBack, setSendingBack] = useState<AdminCheckRow | null>(null);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);

  const type = (config ? checkType : 'dbs') as PreflightKey;
  const list = useAdminChecks(
    type,
    { q: q || undefined, stage: stage || undefined, page, limit: PAGE_SIZE },
    !!config,
  );
  const apply = useMarkCheckApplied(type);
  const process = useMarkCheckProcessed(type);
  const confirm = useConfirmCheck(type);
  const sendBack = useSendBackCheck(type);

  if (!config) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-8">
        <EmptyState
          icon={<ShieldCheck className="h-6 w-6" />}
          title="Unknown check type"
          description="This screening check does not exist."
        />
      </main>
    );
  }

  async function onApply(row: AdminCheckRow) {
    setBusyTaskId(row.task_id);
    try {
      await apply.mutateAsync({ personId: row.person.id, taskId: row.task_id });
      toast.success(`${row.person.full_name}: recorded as applied`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'could not record the application');
    } finally {
      setBusyTaskId(null);
    }
  }

  async function onProcess(row: AdminCheckRow) {
    setBusyTaskId(row.task_id);
    try {
      await process.mutateAsync({ personId: row.person.id, taskId: row.task_id });
      toast.success(`${row.person.full_name}: marked processed`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'could not mark processed');
    } finally {
      setBusyTaskId(null);
    }
  }

  async function onVerify(row: AdminCheckRow) {
    setBusyTaskId(row.task_id);
    try {
      await confirm.mutateAsync({ personId: row.person.id, taskId: row.task_id });
      toast.success(`${row.person.full_name}: check complete`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'could not complete the check');
    } finally {
      setBusyTaskId(null);
    }
  }

  async function onSendBack(reason: string, recipient: RejectEmailRecipient) {
    if (!sendingBack) return;
    const row = sendingBack;
    setBusyTaskId(row.task_id);
    try {
      await sendBack.mutateAsync({
        personId: row.person.id,
        taskId: row.task_id,
        reason,
        recipient,
      });
      const to =
        recipient === 'work'
          ? row.person.email
          : row.person.personal_email ?? row.person.email;
      toast.success(`Sent back. The reason will be emailed to ${to}.`);
      setSendingBack(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'could not send back');
    } finally {
      setBusyTaskId(null);
    }
  }

  const data = list.data;
  return (
    <main className="mx-auto max-w-4xl space-y-4 px-4 py-8">
      <PageHeader
        eyebrowIcon={ShieldCheck}
        eyebrow="HR"
        title={config.title}
        description={config.description}
      />

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-56 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name or email"
            className="block w-full rounded-token-sm border border-line-strong/20 bg-surface py-2 pl-8 pr-3 text-sm"
          />
        </label>
        <div className="flex flex-wrap gap-1">
          <StagePill label="All" active={stage === ''} onClick={() => { setStage(''); setPage(1); }} />
          {CHECK_STAGES.map((s) => (
            <StagePill
              key={s}
              label={CHECK_STAGE_LABEL[s]}
              active={stage === s}
              onClick={() => { setStage(s); setPage(1); }}
            />
          ))}
        </div>
      </div>

      {list.error ? (
        <div className="flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{list.error instanceof ApiError ? list.error.message : 'load failed'}</span>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-token border border-line/10 bg-surface shadow-token-sm">
        {list.isLoading || !data ? (
          <SkeletonList rows={5} />
        ) : data.rows.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" />}
            title={q || stage ? 'No matches' : 'Nobody needs this check yet'}
            description={
              q || stage
                ? 'Try a different search or stage filter.'
                : 'People appear here as soon as the check task is assigned to them (directly or via a role template).'
            }
          />
        ) : (
          <ul className="divide-y divide-divider/10">
            {data.rows.map((row) => (
              <CheckRow
                key={row.task_id}
                row={row}
                busy={busyTaskId === row.task_id}
                onApply={() => onApply(row)}
                onProcess={() => onProcess(row)}
                onVerify={() => setVerifying(row)}
                onSendBack={() => setSendingBack(row)}
              />
            ))}
          </ul>
        )}
      </div>

      {data ? (
        <Pager page={data.page} limit={data.limit} total={data.total} onPage={setPage} noun="people" />
      ) : null}

      <ConfirmModal
        open={!!verifying}
        title="Certificate physically checked?"
        message={
          verifying
            ? `Confirm you are holding ${verifying.person.full_name}'s ORIGINAL certificate and have inspected it in person. Scans, photos, and copies do not count. This completes their ${config.title.replace(/s$/, '')} and locks the pre-employment gate.`
            : ''
        }
        tone="warning"
        confirmLabel="Yes, verified in person"
        onConfirm={async () => {
          if (verifying) await onVerify(verifying);
          setVerifying(null);
        }}
        onClose={() => setVerifying(null)}
      />

      <RejectTaskModal
        open={!!sendingBack}
        taskTitle={sendingBack?.title ?? ''}
        workEmail={sendingBack?.person.email ?? ''}
        personalEmail={sendingBack?.person.personal_email ?? null}
        busy={sendBack.isPending}
        onCancel={() => setSendingBack(null)}
        onSubmit={onSendBack}
      />
    </main>
  );
}

function StagePill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-fg-on-accent'
          : 'rounded-full bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg-muted hover:text-fg'
      }
    >
      {label}
    </button>
  );
}
